import { useEffect, useState } from "react";
import { Rocket, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";

// Milestone anchors for a change that has a System Rollout plan. Editing a date reflows everything hanging off it
// and reports what shifted — a cutover slipping must never silently leave training on the old date.
export default function RolloutPanel({ changeId }) {
  const [r, setR] = useState(undefined);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [shifted, setShifted] = useState(null);
  const load = () => api.get(`/changes/${changeId}/rollout`).then((res) => { setR(res.data.rollout); setDraft((res.data.rollout?.milestones) || {}); }).catch(() => setR(null));
  useEffect(() => { load(); }, [changeId]);
  if (r === undefined || r === null) return null;

  const save = async () => {
    setBusy(true); setShifted(null);
    try {
      const { data } = await api.patch(`/changes/${changeId}/rollout/milestones`, { milestones: draft });
      setShifted(data.shifted);
      toast.success(data.shifted.length ? `${data.shifted.length} item${data.shifted.length === 1 ? "" : "s"} reflowed to the new dates` : "Milestones saved");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="pp-card p-6" data-testid="rollout-panel">
      <div className="flex items-center gap-2"><Rocket size={16} className="text-accent-600" /><h3 className="font-head text-lg font-extrabold">{r.name}</h3></div>
      <p className="text-xs text-warning-800 bg-warning-50 border border-warning-200 rounded-lg px-3 py-2 mt-2" data-testid="rollout-panel-label">{r.label}</p>

      <div className="mt-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2 mb-2"><CalendarClock size={13} /> Milestone anchors — the project's dates this plan hangs off</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {(r.anchors || []).map((a) => (
            <label key={a} className="text-xs"><span className="block text-muted-foreground mb-0.5">{r.anchor_labels?.[a] || a}</span>
              <input type="date" data-testid={`rollout-panel-anchor-${a}`} className="pp-input py-1 text-xs" value={draft[a] || ""} onChange={(e) => setDraft({ ...draft, [a]: e.target.value })} /></label>
          ))}
        </div>
        <button data-testid="rollout-panel-save" onClick={save} disabled={busy} className="pp-btn-dark mt-3 py-1.5 text-sm">{busy ? "Reflowing…" : "Save & reflow plan"}</button>
        {shifted && shifted.length > 0 && (
          <div className="mt-3 rounded-lg border border-border bg-muted p-3 text-xs" data-testid="rollout-shifted">
            <div className="font-bold text-foreground mb-1">What moved</div>
            <ul className="space-y-0.5">{shifted.slice(0, 8).map((s, i) => <li key={i} className="text-muted-foreground"><span className="font-semibold text-foreground">{s.title}</span>: {s.from || "unscheduled"} → {s.to || "unscheduled"}</li>)}</ul>
          </div>
        )}
      </div>

      <div className="mt-5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">The change arc (people work throughout)</div>
        <ol className="space-y-1.5 text-xs">
          {(r.arc || []).map((a) => (
            <li key={a.key} className="flex flex-col sm:flex-row sm:gap-2" data-testid={`rollout-arc-${a.key}`}>
              <span className="font-semibold text-foreground sm:w-44 sm:shrink-0">{a.name}</span>
              <span className="text-muted-foreground flex-1 min-w-0">{a.blurb}</span>
              <span className="text-accent-700 tabular-nums sm:shrink-0">{a.date || `rel. ${a.label}`}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
